import type { SiteEntity } from '../../types/site';
import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { ScreeningOutcome, SiteComposite } from '../../types/portfolioComposite';
import { analysisReadinessDecisionV1Schema } from '../../types/decisionAnalysis';
import { deriveIntentForSelectedAssessment } from '../generationImpact/selectedAssessmentIntent';
import { deriveScreeningArchitectureRecommendations } from '../findings/screeningArchitectureRecommendations';
import { normaliseStateToCode } from '../inferSiteState';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';
import { effectivePower, effectivePowerMwRange } from '../projectIntent';
import { reportsForSite } from '../siteReportLink';
import { reportCanContributeSavedResult, reportCompletesScreening } from '../pathToGo/reportPlan';
import type { CompositeAnalysisContext } from './compositeScorer';
import { deriveSiteVerdict } from './deriveSiteVerdict';

export type PortfolioSiteSort = 'updated' | 'name' | 'area' | 'capacity' | 'recommended-capacity' | 'screening-outcome';
export interface PortfolioSiteViewState {
  sort: PortfolioSiteSort;
  state: string;
  projectPhase: string;
  decisionStage: string;
  architecture: string;
  powerType: string;
  cooling: string;
  recommendationMode: 'all' | 'adopted' | 'recommended';
}
export type SiteListFilterKey = Exclude<keyof PortfolioSiteViewState, 'sort'>;
export interface SiteListOption { value: string; label: string }
export interface SiteListFilter { key: SiteListFilterKey; label: string; options: SiteListOption[] }
export const DEFAULT_SITE_VIEW: Readonly<PortfolioSiteViewState> = Object.freeze({
  sort: 'updated', state: 'all', projectPhase: 'all', decisionStage: 'all',
  architecture: 'all', powerType: 'all', cooling: 'all', recommendationMode: 'all',
});
export const SITE_SORT_OPTIONS: readonly { value: PortfolioSiteSort; label: string }[] = [
  { value: 'updated', label: 'Recently updated' }, { value: 'name', label: 'Site name' },
  { value: 'area', label: 'Area · largest first' }, { value: 'capacity', label: 'Chosen capacity · upper bound' },
  { value: 'recommended-capacity', label: 'Recommended capacity · upper bound' },
  { value: 'screening-outcome', label: 'Screening outcome' },
];

export interface SiteListChoices {
  architecture: string[];
  powerType: string[];
  cooling: string[];
}
export interface SiteListCapacity { min: number; max: number; basis: 'it-load' | 'facility' | 'unknown' }
export interface PortfolioSiteMetadata {
  site: SiteEntity;
  id: string;
  areaAcres: number | null;
  state: string | null;
  projectPhases: string[];
  decisionStage: 'stage1' | 'interlock' | 'stage2' | 'decided';
  screeningOutcome: ScreeningOutcome | null;
  screeningCompleted: boolean;
  powerLabel: string;
  adoptedCapacity: SiteListCapacity | null;
  recommendedCapacity: SiteListCapacity | null;
  recommendedCapacityLabel: string | null;
  recommendationReportId: string | null;
  recommendationCurrency: 'current' | 'saved' | null;
  adopted: SiteListChoices;
  recommended: SiteListChoices;
  updatedAtMs: number | null;
}

const UNKNOWN = 'Unknown / undecided';
const LABELS: Record<string, string> = {
  'grid-only': 'Grid only', hybrid: 'Hybrid grid + BTM', 'btm-only': 'BTM only',
  'islanded': 'Islanded (off-grid)',
  'grid-led-standby': 'Grid-led with emergency standby', grid: 'Utility grid',
  recip: 'Reciprocating gas engines', 'gas-cc': 'Combined-cycle gas', 'gas-sc': 'Simple-cycle gas',
  'gas-turbine': 'Gas turbine · cycle unspecified', battery: 'Battery storage',
  'solar-bess': 'Solar + storage', 'wind-bess': 'Wind + storage', smr: 'Small modular nuclear',
  'large-nuclear': 'Large nuclear', 'loop:air': 'Air cooling', 'loop:liquid': 'Liquid cooling',
  'loop:hybrid': 'Hybrid air + liquid cooling', 'heat:dry-cooler': 'Dry heat rejection',
  'heat:wet-cooling-tower': 'Wet cooling tower', 'heat:hybrid': 'Hybrid heat rejection',
  'heat:once-through': 'Once-through cooling', 'heat:free-cooling': 'Free cooling',
  'heat:absorption-cchp': 'Absorption CCHP', 'single-phase': 'Single build phase',
  'multi-phase': 'Multiple build phases', stage1: 'Stage 1 · Screening',
  interlock: 'Generation scenario', stage2: 'Stage 2 · Validation', decided: 'Decided',
};
const unique = (values: readonly string[]): string[] => [...new Set(values)].sort();
const positive = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
const timestamp = (value: string | undefined): number | null => {
  const parsed = value ? Date.parse(value) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

/** Read the canonical address, never the editable marketing label or site name. */
function stateOf(site: SiteEntity): string | null {
  const address = site.address ?? '';
  const parts = address.split(',').map(part => part.trim()).filter(Boolean);
  for (const part of parts.reverse()) {
    const state = normaliseStateToCode(part.replace(/\s+\d{5}(?:-\d{4})?$/, ''));
    if (state) return state;
  }
  const suffix = /\b([A-Z]{2})\s+\d{5}(?:-\d{4})?(?:\s+(?:USA|US|United States))?$/i.exec(address.trim());
  return suffix ? normaliseStateToCode(suffix[1]) : null;
}

/** The same immutable-body/hold/readiness boundary used by saved screening advice.
 * A completed run is sufficient; global Stage 1 milestones and Intent are not
 * prerequisites for displaying a recommendation. No network or model runs. */
export function completedScreeningReportForSite(site: SiteEntity, reports: readonly SiteRagReport[]): SiteRagReport | null {
  return reportsForSite(site, reports).filter(report => {
    if (report.siteId != null && report.siteId !== site.id) return false;
    if (!reportCanContributeSavedResult(report, 'feasibility') || report.qualityHold === 'needs-revision'
      || (report.qualityHoldFindings?.length ?? 0) > 0) return false;
    const raw = report.analysisReadiness ?? report.analysisRevision?.readiness;
    if (raw == null) return true;
    const parsed = analysisReadinessDecisionV1Schema.safeParse(raw);
    return parsed.success && parsed.data.canView && parsed.data.identityValid && parsed.data.qualityReady
      && parsed.data.narrativeReady && ((parsed.data.ready && parsed.data.canUseForDecision)
        || parsed.data.state === 'queued' || parsed.data.state === 'refreshing');
  }).sort((a, b) => (timestamp(b.updatedAt ?? b.createdAt) ?? 0) - (timestamp(a.updatedAt ?? a.createdAt) ?? 0)
    || a.id.localeCompare(b.id, 'en'))[0] ?? null;
}

function adoptedChoices(site: SiteEntity, intent: ProjectIntent | undefined | null): SiteListChoices {
  const architecture: string[] = [];
  const strategy = site.typedPreferencesDefaults?.['power-availability']?.btmStrategy;
  if (site.generationAssessmentSelection?.kind === 'grid-led-standby-planning') architecture.push('grid-led-standby');
  else if (strategy === 'grid-only' || strategy === 'hybrid' || strategy === 'btm-only' || strategy === 'islanded') architecture.push(strategy);
  const powerType: string[] = [];
  for (const phase of isIntentFieldExplicitlyUnknown(intent, 'phases') ? [] : intent?.phases ?? []) {
    if (phase.powerSourceConfig?.gridConnection === 'islanded') architecture.push('islanded');
    const components = phase.powerSourceConfig?.components;
    if (components?.length) powerType.push(...components);
    else if (phase.powerSource && phase.powerSource !== 'undecided' && phase.powerSource !== 'hybrid') powerType.push(phase.powerSource);
  }
  const cooling: string[] = [];
  if (intent?.coolingStrategy && intent.coolingStrategy !== 'undecided' && !isIntentFieldExplicitlyUnknown(intent, 'coolingStrategy')) cooling.push(`loop:${intent.coolingStrategy}`);
  if (intent?.bopHeatRejection && intent.bopHeatRejection !== 'unknown' && !isIntentFieldExplicitlyUnknown(intent, 'bopHeatRejection')) cooling.push(`heat:${intent.bopHeatRejection}`);
  return { architecture: unique(architecture), powerType: unique(powerType), cooling: unique(cooling) };
}

function recommendationRange(value: string | undefined): SiteListCapacity | null {
  if (!value) return null;
  // Only the already validated dedicated saved range enters here. Declared
  // pathway MW, prose quantities and max-power ceilings never reach this path.
  const clean = value.replace(/^(?:Recommended screening (?:size|capacity|load) range|Screening[- ]recommended (?:size|capacity|load) range)\s*:\s*/i, '');
  const number = '(?:\\d{1,3}(?:,\\d{3})+|\\d+)(?:\\.\\d+)?';
  const match = new RegExp(`^(${number})\\s*(?:[–—-]|to)\\s*(${number})\\s*MW\\s+(?:of\\s+)?(IT|facility)\\b`, 'i').exec(clean);
  if (!match) return null;
  const min = positive(Number(match[1]!.replaceAll(',', '')));
  const max = positive(Number(match[2]!.replaceAll(',', '')));
  return min != null && max != null && max > min ? { min, max, basis: match[3]!.toLowerCase() === 'it' ? 'it-load' : 'facility' } : null;
}

function stationOf(composite: SiteComposite | null | undefined): PortfolioSiteMetadata['decisionStage'] {
  if (!composite) return 'stage1';
  if (deriveSiteVerdict(composite, null).band != null) return 'decided';
  const stages = composite.decisionStages;
  if (!stages?.screening.presentTypes.includes('feasibility')) return 'stage1';
  if (composite.decisionHold?.code === 'power-strategy-undeclared') return 'interlock';
  const complete = stages.screening.requiredTypes.length > 0
    && stages.screening.presentTypes.length === stages.screening.requiredTypes.length;
  if (complete) return stages.validation.locked ? 'interlock' : 'stage2';
  if (composite.decisionHold?.code === 'stage-2-locked') return 'interlock';
  if (composite.decisionHold?.code === 'stage-2-incomplete') return 'stage2';
  if (stages.validation.presentTypes.length > 0) return 'stage2';
  return 'stage1';
}

export function deriveSiteListMetadata(site: SiteEntity, reports: readonly SiteRagReport[], composite: SiteComposite | null | undefined,
  analysisContext?: CompositeAnalysisContext): PortfolioSiteMetadata {
  let intent: ProjectIntent | undefined | null;
  try { intent = deriveIntentForSelectedAssessment(site.projectIntent, site.generationAssessmentSelection); }
  catch { intent = null; } // A malformed selected pointer cannot revive inactive choices.
  const power = effectivePower(intent ? { powerRequirement: isIntentFieldExplicitlyUnknown(intent, 'powerRequirement') ? 'undecided' : intent.powerRequirement,
    powerRequirementMW: isIntentFieldExplicitlyUnknown(intent, 'powerRequirementMW') ? undefined : intent.powerRequirementMW } : intent); // SSOT-SENTINEL-OK — canonical ProjectIntent read for effectivePower, never report preferences.
  const range = effectivePowerMwRange(power);
  const adoptedCapacity = range && positive(range.max) != null && Number.isFinite(range.min) && range.min >= 0
    ? { ...range, basis: isIntentFieldExplicitlyUnknown(intent, 'powerBasis') ? 'unknown' as const : intent?.powerBasis ?? 'unknown' as const } : null;
  const phases = isIntentFieldExplicitlyUnknown(intent, 'phases') ? [] : intent?.phases ?? [];
  const projectPhases = phases.length ? unique([phases.length > 1 ? 'multi-phase' : 'single-phase',
    ...phases.flatMap(phase => Number.isInteger(phase.sequence) && phase.sequence > 0 ? [`phase:${phase.sequence}`] : [])]) : [];
  const report = completedScreeningReportForSite(site, reports);
  const advice = deriveScreeningArchitectureRecommendations(report, site.id);
  const recommended: SiteListChoices = {
    architecture: advice['btm-architecture']?.strategy ? [advice['btm-architecture']!.strategy!] : [],
    powerType: unique(advice['power-type']?.supplyOptions ?? advice['btm-architecture']?.supplyOptions ?? []),
    cooling: unique([...(advice['cooling-strategy']?.coolingStrategy ? [`loop:${advice['cooling-strategy']!.coolingStrategy}`] : []),
      ...(advice['cooling-type']?.heatRejection ? [`heat:${advice['cooling-type']!.heatRejection}`] : [])]),
  };
  const current = report ? reportCompletesScreening(report, 'feasibility', { site, sed: null, intent: intent ?? undefined,
    requiresGenerationImpact: false, analysisGateEnabled: analysisContext?.analysisGateEnabled }) : false;
  const aggregate = site.siteBoundaryAggregate?.totalArea;
  const area = typeof aggregate === 'number' && Number.isFinite(aggregate) ? aggregate : site.siteArea;
  return { site, id: site.id, areaAcres: typeof area === 'number' && Number.isFinite(area) && area >= 0 ? area : null, state: stateOf(site),
    projectPhases, decisionStage: stationOf(composite), screeningCompleted: report != null,
    screeningOutcome: report ? composite?.decisionStages?.screening.outcome ?? null : null,
    powerLabel: power.displayLabel,
    adoptedCapacity, recommendedCapacity: recommendationRange(advice['dc-power']?.value),
    recommendedCapacityLabel: advice['dc-power']?.value ?? null,
    recommendationReportId: Object.keys(advice).length ? report?.id ?? null : null,
    recommendationCurrency: Object.keys(advice).length ? current ? 'current' : 'saved' : null,
    adopted: intent === null ? { architecture: [], powerType: [], cooling: [] } : adoptedChoices(site, intent),
    recommended, updatedAtMs: timestamp(site.updatedAt) };
}

function choicesFor(item: PortfolioSiteMetadata, mode: PortfolioSiteViewState['recommendationMode'], key: keyof SiteListChoices): string[] {
  return mode === 'adopted' ? item.adopted[key] : mode === 'recommended' ? item.recommended[key] : unique([...item.adopted[key], ...item.recommended[key]]);
}
const matches = (values: readonly string[], filter: string): boolean => filter === 'all' || (filter === 'unknown' ? values.length === 0 : values.includes(filter));
const descendingKnown = (a: number | null, b: number | null): number => a == null ? b == null ? 0 : 1 : b == null ? -1 : b - a;
export function selectSiteList(items: readonly PortfolioSiteMetadata[], state: PortfolioSiteViewState): PortfolioSiteMetadata[] {
  return items.filter(item => matches(item.state ? [item.state] : [], state.state)
    && matches(item.projectPhases, state.projectPhase) && matches([item.decisionStage], state.decisionStage)
    && (['architecture', 'powerType', 'cooling'] as const).every(key => matches(choicesFor(item, state.recommendationMode, key), state[key])))
    .sort((a, b) => {
      let order = 0;
      if (state.sort === 'updated') order = descendingKnown(a.updatedAtMs, b.updatedAtMs);
      if (state.sort === 'area') order = descendingKnown(a.areaAcres, b.areaAcres);
      if (state.sort === 'capacity') order = descendingKnown(a.adoptedCapacity?.max ?? null, b.adoptedCapacity?.max ?? null);
      if (state.sort === 'recommended-capacity') order = descendingKnown(a.recommendedCapacity?.max ?? null, b.recommendedCapacity?.max ?? null);
      if (state.sort === 'screening-outcome') {
        const rank: Record<ScreeningOutcome, number> = { clear: 0, concerns: 1, declined: 2 };
        order = descendingKnown(a.screeningOutcome ? -rank[a.screeningOutcome] : null, b.screeningOutcome ? -rank[b.screeningOutcome] : null);
      }
      return order || a.site.name.localeCompare(b.site.name, 'en', { sensitivity: 'base' }) || a.id.localeCompare(b.id, 'en');
    });
}

export function buildSiteListOptions(items: readonly PortfolioSiteMetadata[]): SiteListFilter[] {
  const descriptor = (key: SiteListFilterKey, label: string, values: string[]): SiteListFilter => ({ key, label, options: [
    { value: 'all', label: 'All' }, { value: 'unknown', label: UNKNOWN },
    ...unique(values).map(value => ({ value, label: LABELS[value] ?? (value.startsWith('phase:') ? `Build phase ${value.slice(6)}` : value) })),
  ] });
  return [descriptor('state', 'US state', items.flatMap(item => item.state ? [item.state] : [])),
    descriptor('projectPhase', 'Build phase', items.flatMap(item => item.projectPhases)),
    descriptor('decisionStage', 'Decision stage', items.map(item => item.decisionStage)),
    ...(['architecture', 'powerType', 'cooling'] as const).map(key => descriptor(key,
      key === 'architecture' ? 'Power architecture' : key === 'powerType' ? 'Power type' : 'Cooling',
      items.flatMap(item => [...item.adopted[key], ...item.recommended[key]]))),
    { key: 'recommendationMode', label: 'Choice basis', options: [{ value: 'all', label: 'Chosen or recommended' },
      { value: 'adopted', label: 'Chosen inputs only' }, { value: 'recommended', label: 'Screening recommendations only' }] }];
}
